import * as React from 'react'
import { parseSetupMarkdown, type InlineSpan, type SetupBlock } from '@lib/helpers/setup-markdown'

/**
 * Instructions, rendered as React elements and never as HTML.
 *
 * The parser returns a tree of text spans, so there is no HTML string anywhere
 * in this path and nothing to hand to `dangerouslySetInnerHTML`. A `<script>`
 * typed into the editor arrives here as the four words somebody typed and is
 * drawn as text — not because anything sanitises it, but because text is the
 * only thing the parser can produce. That matters: this is the largest block of
 * free text on an unauthenticated page — a plan's notes and captions reach it
 * too — and a client may be the one writing it. See `docs/public-schedule.md`.
 */
function Spans({ spans }: { spans: InlineSpan[] }): React.ReactElement {
  return (
    <>
      {/* Keyed by position: a span has no identity of its own — it is a parse of
          one line, replaced whole whenever that line changes. */}
      {spans.map((span, index) =>
        span.bold ? (
          <strong key={index} className="font-semibold">
            {span.text}
          </strong>
        ) : (
          <React.Fragment key={index}>{span.text}</React.Fragment>
        ),
      )}
    </>
  )
}

function Block({ block }: { block: SetupBlock }): React.ReactElement {
  if (block.kind === 'paragraph') {
    return (
      <p className="text-sm leading-relaxed text-ink">
        <Spans spans={block.spans} />
      </p>
    )
  }
  const items = block.items.map((spans, index) => (
    <li key={index} className="text-sm leading-relaxed text-ink">
      <Spans spans={spans} />
    </li>
  ))
  return block.kind === 'bullets' ? (
    <ul className="list-disc space-y-1 pl-5">{items}</ul>
  ) : (
    <ol className="list-decimal space-y-1 pl-5">{items}</ol>
  )
}

export function SetupInstructions({ source }: { source: string }): React.ReactElement | null {
  const blocks = React.useMemo(() => parseSetupMarkdown(source), [source])
  if (blocks.length === 0) return null
  return (
    <div className="space-y-3">
      {blocks.map((block, index) => (
        <Block key={index} block={block} />
      ))}
    </div>
  )
}
